/**
 * STYLING: Plain Tailwind ONLY!
 */

import { Coins } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GOOGLE_SEARCH_SURCHARGE = 3;

interface TextToolCreditBadgeProps {
  baseCredits: number;
  multipliers: Record<string, number>;
  selectedModel: string;
  promptLength?: number;
  inputSizeTiers?: [number | null, number][];
  googleSearch?: boolean;
}

function getInputMultiplier(length: number, tiers?: [number | null, number][]): number {
  if (!tiers || tiers.length === 0 || length === 0) return 1.0;
  for (const [threshold, mult] of tiers) {
    if (threshold !== null && length < threshold) return mult;
  }
  const lastTier = tiers[tiers.length - 1];
  return lastTier[0] === null ? lastTier[1] : 1.0;
}

export function TextToolCreditBadge({ baseCredits, multipliers, selectedModel, promptLength = 0, inputSizeTiers, googleSearch = false }: TextToolCreditBadgeProps) {
  const { t } = useTranslation();
  const modelMult = multipliers[selectedModel] ?? 1.0;
  const inputMult = getInputMultiplier(promptLength, inputSizeTiers);
  const baseCost = Math.ceil(baseCredits * modelMult * inputMult);
  const effectiveCost = baseCost + (googleSearch ? GOOGLE_SEARCH_SURCHARGE : 0);
  const hasModelMult = modelMult > 1;
  const hasInputMult = inputMult > 1;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Coins className="h-4 w-4 text-amber-500" />
      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
        {effectiveCost} {t('textTools.credits')}
      </span>
      {hasModelMult && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          {modelMult}x {t('textTools.pro')}
        </span>
      )}
      {hasInputMult && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
          {inputMult}x {t('textTools.inputLabel')}
        </span>
      )}
      {googleSearch && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
          +{GOOGLE_SEARCH_SURCHARGE} Search
        </span>
      )}
      {!hasModelMult && !hasInputMult && !googleSearch && baseCredits > 0 && (
        <span className="text-xs text-neutral-500 dark:text-neutral-400">{t('textTools.flash')}</span>
      )}
    </div>
  );
}
